import { EventPropertyType } from './Index';
export var Version = '2.0.5';
export var FullVersionString = '1DS-Web-JS-' + Version;
export var Undefined = 'undefined';
var beaconsSupported = null;
var uInt8ArraySupported = null;
var areCookiesAvailable = Boolean(typeof document !== 'undefined' && document) || Boolean(typeof navigator !== 'undefined' && navigator.cookieEnabled);
/**
* Checks if document object is available
*/
export var isDocumentObjectAvailable = (typeof document !== 'undefined' && document) ? true : false;
/**
* Checks if window object is available
*/
export var isWindowObjectAvailable = (typeof window !== 'undefined' && window) ? true : false;
/**
* Checks if value is assigned to the given param.
* @param {object} value - The token from which the tenant id is to be extracted.
* @return {boolean} True/false denoting if value is assigned to the param.
*/
export function isValueAssigned(value) {
    /// <summary> takes a value and checks for undefined, null and empty string </summary>
    /// <param type="any"> value to be tested </param>
    /// <returns> true if value is null undefined or emptyString </returns>
    return !(value === undefined || value === null || value === '');
}
/**
 * Checks if the type of value is a string.
 * @param {any} value - Value to be checked.
 * @return {boolean} True if the value is a string, false otherwise.
 */
export function isString(value) {
    return typeof value === 'string';
}
/**
 * Checks if the type of value is a number.
 * @param {any} value - Value to be checked.
 * @return {boolean} True if the value is a number, false otherwise.
 */
export function isNumber(value) {
    return typeof value === 'number';
}
/**
 * Checks if the type of value is a boolean.
 * @param {any} value - Value to be checked.
 * @return {boolean} True if the value is a boolean, false otherwise.
 */
export function isBoolean(value) {
    return typeof value === 'boolean';
}
/**
 * Gets the tenant id from the tenant token.
 * @param {string} apiKey - The token from which the tenant id is to be extracted.
 * @return {string} The tenant id.
 */
export function getTenantId(apiKey) {
    if (apiKey) {
        var indexTenantId = apiKey.indexOf('-');
        if (indexTenantId > -1) {
            return apiKey.substring(0, indexTenantId);
        }
    }
    return '';
}
/**
 * Checks if HTML5 Beacons are supported in the current environment.
 * @return {boolean} True if supported, false otherwise.
 */
export function isBeaconsSupported() {
    if (beaconsSupported === null) {
        beaconsSupported = typeof navigator !== Undefined && Boolean(navigator.sendBeacon);
    }
    return beaconsSupported;
}
/**
 * Checks if Uint8Array are available in the current environment. Safari and Firefox along with
 * ReactNative are known to not support Uint8Array properly.
 * @return {boolean} True if available, false otherwise.
 */
export function isUint8ArrayAvailable() {
    if (uInt8ArraySupported === null) {
        uInt8ArraySupported = typeof Uint8Array !== Undefined && !isSafariOrFirefox() && !isReactNative();
    }
    return uInt8ArraySupported;
}
/**
 * Checks if the value is a valid EventLatency.
 * @param {enum} value - The value that needs to be checked.
 * @return {boolean} True if the value is in AWTEventLatency, false otherwise.
 */
export function isLatency(value) {
    if (value && isNumber(value) && value >= 1 && value <= 3) {
        return true;
    }
    return false;
}
/**
 * Sanitizes the Property. It checks the that the property name and value are valid. It also
 * checks/populates the correct type and pii of the property value.
 * @param {string} name                           - The property name.
 * @param {string|number|boolean|object} property - The property value or an IEventProperty containing value,
 * type ,pii and customer content.
 * @return {object} IEventProperty containing valid name, value, pii and type or null if invalid.
 */
export function sanitizeProperty(name, property, stringifyObjects) {
    //Check that property is valid
    if (!isString(name) || property === undefined || property === null || property === '') {
        return null;
    }
    //If the property isn't IEventProperty (and is either string, number, boolean or array), convert it into one.
    if (isString(property) || isNumber(property) || isBoolean(property) || isArray(property)) {
        property = { value: property };
    }
    else if (typeof property === 'object' && !property.value) {
        property = { value: stringifyObjects ? JSON.stringify(property) : property };
    }
    else if (property.value === undefined || property.value === null
        || property.value === '' || (!isString(property.value)
        && !isNumber(property.value) && !isBoolean(property.value)
        && !isArray(property.value))) {
        //Since property is IEventProperty, we need to validate its value
        return null;
    }
    //We need to check that if the property value is an array, it is valid
    if (isArray(property.value) &&
        !isArrayValid(property.value)) {
        return null;
    }
    //If either pii or cc is set convert value to string (since only string pii/cc is allowed).
    //If the value is a complex type like an array that can't be converted to string we will drop
    //the property.
    if (property.kind !== undefined) {
        if (isArray(property.value) || !isValueKind(property.kind)) {
            return null;
        }
        property.value = property.value.toString();
    }
    return property;
}
/**
 * Converts a date object into an ISO string. This is needed because not all browsers support ISO string format
 * on the date.
 * @param {object} date - The date which needs to be converted to ISO format.
 * @return {string} The date in ISO format.
 */
export function getISOString(date) {
    return date.getUTCFullYear() + '-' +
        twoDigit(date.getUTCMonth() + 1) + '-' +
        twoDigit(date.getUTCDate()) + 'T' +
        twoDigit(date.getUTCHours()) + ':' +
        twoDigit(date.getUTCMinutes()) + ':' +
        twoDigit(date.getUTCSeconds()) + '.' +
        threeDigit(date.getUTCMilliseconds()) + 'Z';
}
export function isReactNative() {
    // If running in React Native, navigator.product will be populated
    if (typeof navigator !== Undefined && navigator.product) {
        return navigator.product === 'ReactNative';
    }
    return false;
}
export function useXDomainRequest() {
    if (typeof XMLHttpRequest !== 'undefined') {
        var conn = new XMLHttpRequest();
        return Boolean(typeof conn.withCredentials === 'undefined' &&
            typeof XDomainRequest !== 'undefined');
    }
}
export function isArray(value) {
    return value instanceof Array;
}
export function getCommonSchemaMetaData(value, kind, type) {
    var encodedTypeValue = -1;
    if (value === undefined) {
        return encodedTypeValue;
    }
    if (kind && kind > 0) {
        if (kind === 32) {
            //encode customer content. Value can only be string.
            encodedTypeValue = _getCCEncodedValue(1);
        }
        else if (kind <= 13) {
            //encode PII. Value can only be string
            encodedTypeValue = _getPiiEncodedValue(kind);
        }
    }
    if (type !== undefined && isDataType(type)) {
        // Data Type is provided and valid, so use that
        if (encodedTypeValue === -1) {
            // Don't return -1
            encodedTypeValue = 0;
        }
        return encodedTypeValue | type;
    }
    else {
        //If value is array just get the type for the first element
        if (isArray(value)) {
            value = value[0];
        }
        if (encodedTypeValue !== -1) {
            //pii exists so we must return correct type
            return encodedTypeValue | _getTypeEncodedValue(value);
        }
        else if (isNumber(value)) {
            return EventPropertyType.Double;
        }
    }
    return encodedTypeValue;
}
export function cookieAvailable() {
    return areCookiesAvailable;
}
/**
* Sets the value of a cookie.
* @param {string} name - Cookie name.
* @param {string} value - Cookie value.
* @param {number} days - Expiration days.
*/
export function setCookie(name, value, days) {
    if (areCookiesAvailable) {
        var date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        var expires = '; expires=' + date.toUTCString();
        window.document.cookie = name + '=' + value + expires + '; path=/;';
    }
}
/**
* Deletes a cookie, by setting its expiration to -1.
* @param {string} name - Cookie name to delete.
*/
export function deleteCookie(name) {
    setCookie(name, '', -1);
}
/**
* Gets the cookie value for the specified cookie.
* if value is k1=v1&k2==v2 then will return 'v1' for key 'k1'
* @param {string} cookieName - Cookie name.
*/
export function getCookie(name) {
    if (areCookiesAvailable) {
        name = name + '=';
        var decodedCookie = decodeURIComponent(document.cookie);
        var ca = decodedCookie.split(';');
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            var j = 0;
            while (c.charAt(j) === ' ') {
                j++;
            }
            c = c.substring(j);
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
    }
    return '';
}
export function createGuid() {
    var guidPattern = 'xxxxxxxx-xxxx-4xxx-Rxxx-xxxxxxxxxxxx';
    function randomHexDigit() {
        return Math.floor(Math.random() * 16).toString(16);
    }
    var result = guidPattern.replace(/x/g, randomHexDigit);
    // update remaining 2 bits of first digit of the clock_seq_hi_and_reserved:
    return result.replace('R', (8 | Math.floor(Math.random() * 3)).toString(16));
}
export function extend(obj, obj2, obj3, obj4, obj5) {
    /// <summary> Pass in the objects to merge as arguments </summary>
    /// <param type='Object'> pass in as many objects as you want. For a deep extend, set the first argument to `true`</param>
    /// <returns> extended object </returns>
    // Variables
    var extended = {};
    var deep = false;
    var i = 0;
    var length = arguments.length;
    // Check if a deep merge
    if (Object.prototype.toString.call(arguments[0]) === '[object Boolean]') {
        deep = arguments[0];
        i++;
    }
    // Merge the object into the extended object
    var merge = function (obj) {
        for (var prop in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                // If deep merge and property is an object, merge properties
                if (deep && Object.prototype.toString.call(obj[prop]) === '[object Object]') {
                    extended[prop] = extend(true, extended[prop], obj[prop]);
                }
                else {
                    extended[prop] = obj[prop];
                }
            }
        }
    };
    // Loop through each object and conduct a merge
    for (; i < length; i++) {
        var obj = arguments[i];
        merge(obj);
    }
    return extended;
}
function _getTypeEncodedValue(value) {
    if (value === undefined) {
        return -1;
    }
    if (isNumber(value)) {
        //DataType Double has encoded value 6.
        return EventPropertyType.Double;
    }
    else if (isString(value)) {
        //DataType String has encoded value 1.
        return EventPropertyType.String;
    }
    else if (isBoolean(value)) {
        return EventPropertyType.Bool;
    }
    return -1;
}
function _getPiiEncodedValue(pii) {
    //bits 5-12 are for Pii
    return pii << 5;
}
/**
 * Customer Content Types
 */
function _getCCEncodedValue(cc) {
    //bit 13-16 are for cc
    return cc << 13;
}
function twoDigit(n) {
    // Format integers to have at least two digits.
    return n < 10 ? '0' + n : n.toString();
}
function threeDigit(n) {
    // Format integers to have at least three digits.
    if (n < 10) {
        return '00' + n;
    }
    else if (n < 100) {
        return '0' + n;
    }
    return n.toString();
}
function isValueKind(value) {
    if (value && isNumber(value) && ((value >= 0 && value <= 13) || value === 32)) {
        return true;
    }
    return false;
}
function isDataType(value) {
    // Remark: 0 returns false, but it doesnt affect encoding anyways
    if (isNumber(value) && ((value >= 0 && value <= 9))) {
        return true;
    }
    return false;
}
function isSafariOrFirefox() {
    // If non-browser navigator will be undefined
    if (typeof navigator !== Undefined && navigator.userAgent) {
        var ua = navigator.userAgent.toLowerCase();
        if ((ua.indexOf('safari') >= 0 || ua.indexOf('firefox') >= 0) && ua.indexOf('chrome') < 0) {
            return true;
        }
    }
    return false;
}
function isArrayValid(value) {
    return value.length > 0;
}
//# sourceMappingURL=Utils.js.map